import React, { Fragment, useMemo, useState } from 'react';

import { FormTitle, SimpleTextInput } from '@components/admin/Forms';
import { Layout } from '@components/admin/Layout';
import { RoutePlanAddItemDialog } from '@components/admin/widgets/RoutePlanAddItemDialog';
import { WarehouseDisplay } from '@components/admin/widgets/WarehouseDisplay';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogActions,
  ActionableDialogContent,
  ActionableDialogTitle,
} from '@components/Dialog';
import { FormAlert } from '@components/Forms/FormAlert';
import { SubmittingOverlay } from '@components/Forms/SubmittingOverlay';
import { PaginationWithSearchQuery } from '@components/PaginationWithSearchQuery';
import { QueryContainer } from '@components/QueryContainer';
import { agencyStrings } from '@data/agency';
import { getCityLabelByName } from '@data/cities';
import { companyStrings } from '@data/company';
import { routePlanScheduleFields, routePlanStrings, weekdays } from '@data/routePlan';
import { vesselScheduleStrings } from '@data/vesselSchedule';
import { useDeleteRoutePlan } from '@hooks/mutation/dashboard/useDeleteRoutePlan';
import { usePatchRoutePlan } from '@hooks/mutation/dashboard/usePatchRoutePlan';
import { useAgencyList } from '@hooks/queries/dashboard/useAgencyList';
import { useMemberList } from '@hooks/queries/dashboard/useMemberList';
import { useRoutePlan } from '@hooks/queries/dashboard/useRoutePlan';
import { useUser } from '@hooks/queries/useUser';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { useSearch } from '@hooks/useSearch';
import { Member } from '@models/Member';
import {
  RoutePlan,
  routePlanSchedulesForm,
  RoutePlanSchedule,
  routePlanScheduleFormInitialValues,
  RoutePlanScheduleForm,
} from '@models/RoutePlan';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { createValidate, formStrings, joinKeyPath } from '@utils/forms';
import { createStrings } from '@utils/local';
import { getBreadcrumb, url } from '@utils/pages';
import { iota } from '@utils/types';
import clsx from 'clsx';
import Color from 'color';
import { FieldArray, Form, Formik, useField } from 'formik';
import * as t from 'io-ts';
import { pick } from 'lodash-es';
import { Link, useHistory } from 'react-router-dom';
import * as h from 'tyrann-io';
import { unwrap } from 'tyrann-io';

const strings = createStrings({
  ja: {
    routeInfo: `${routePlanStrings.route}情報`,
    managementInfo: '管理情報',
    patchRoutePlanSuccess: '正常に保存しました。',
    disableRoutePlanSuccess: 'ルートを無効にしました。',
    enableRoutePlanSuccess: 'ルートを有効にしました。',
    disableTheRoutePlan: 'このルートを無効にする',
    enableTheRoutePlan: 'このルートを有効にする',
    fcsNo: 'FCS番号',
    details: '詳細',
    editSchedule: 'スケジュールを編集する',
    diablePrompt: () => (
      <>
        このルートを無効にすると、予約システムの予約画面のルート一覧に表示されなくなります。
        <br />
        <br />
        無効にしてもよろしいですか？
      </>
    ),
    routeMembers: `当${routePlanStrings.route}を利用できる会員様`,
    noRouteMembers: () => (
      <>
        当ルートを利用できる会員様はいません。
        <br />
        会員詳細ページから指定してください。
      </>
    ),
    disabledWarning: (
      <>
        当ルートが無効されたので、予約システムの予約画面のルート一覧に表示されなくなりました。
        <br />
        通常予約を確保するため、当ルートをご利用できる会員様の詳細ページから他の有効なルートをご指定ください。
        <br />
      </>
    ),
    editDisabledAdminOnly: 'ルート編集は管理員のみです。',
    editDisabledReasonUsing:
      'ご利用中のお客様がいる・もしくは当ルートが有効なため、当ルートは編集できないことになっています。当ルートを編集したい場合は、このルートを無効にし、ご利用中の会員の詳細ページで当ルートを排除してください。',
    deleteDisabledReasonUsing:
      'ご利用中のお客様がいるため、当ルートは削除できないことになっています。当ルートを削除したい場合は、このルートを無効にし、ご利用中の会員の詳細ページで当ルートを排除してください。',
    deleteTheRoutePlan: 'このルートを削除する',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexInline,
    flex: 1,
    padding: '1.5rem 2rem',
    overflowY: 'hidden',
  },
  leftColumn: {
    ...common.flexColumn,
    padding: '1.5rem 2rem',
    flex: 2.25,
    overflowY: 'scroll',
  },
  rightColumn: {
    ...common.flexColumn,
    padding: '1.5rem 2rem',
    width: 360,
    overflowY: 'scroll',
  },
  routePlanInfoContainer: {
    ...common.middleText,
    ...common.textGray800,
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    border: `1px solid ${Color(theme.palette.primary.main).alpha(0.1).toString()}`,
    '& > div': {
      padding: '15px 20px',
    },
    '& > *[role="head"]': {
      ...common.flexColumnCenter,
      ...common.fontWeight600,
      backgroundColor: Color(theme.palette.primary.main).alpha(0.1).toString(),
    },
    '& > *[role="data"]': {
      position: 'relative',
    },
    '& *[role="divider"]': {
      height: 1,
      width: '100%',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: Color(theme.palette.primary.main).alpha(0.1).toString(),
    },
  },
  routePlanManagememntInfoContainer: {
    ...common.middleText,
    ...common.textGray800,
    ...common.flexColumn,
  },
  routePlanManagementInfoSection: {
    ...common.flexColumn,
    padding: 20,
    border: `1px solid ${theme.palette.grey[200]}`,
    marginBottom: '-1px',
    rowGap: 20,
  },
  routePlanManagementInfoTitle: {
    ...common.fontWeight600,
    ...common.flexInline,
    ...common.justifyBetween,
    ...common.itemsCenter,
  },
  disableButton: {
    backgroundColor: theme.palette.grey[800],
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: Color(theme.palette.grey[800]).lighten(0.1).toString(),
    },
    '&:active': {
      backgroundColor: Color(theme.palette.grey[800]).darken(0.25).toString(),
    },
    '&:disabled': {
      ...common.baseButton,
      backgroundColor: Color(theme.palette.grey[800]).alpha(0.4).toString(),
    },
  },
  routeMembersContainer: {
    ...common.middleText,
    ...common.textGray800,
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    marginBottom: '1rem',
    '& > div': {
      paddingLeft: 22,
      paddingRight: 22,
      borderBottom: `1px solid ${theme.palette.grey[200]}`,
    },
    '& > *[role="head"]': {
      ...common.fontWeight600,
      backgroundColor: theme.palette.grey[50],
      paddingTop: 15,
      paddingBottom: 15,
    },
    '& > *[role="data"]': {
      paddingTop: 12,
      paddingBottom: 12,
    },
  },
  noRouteMembers: {
    ...common.textGray500,
    ...common.fontWeight600,
    ...common.textAlignCenter,
    paddingTop: 36,
    paddingBottom: 36,
  },
  disabledWarning: {
    ...common.smallerText,
    ...common.textGray500,
    ...common.fontWeight600,
    padding: 10,
    marginBottom: 10,
    backgroundColor: Color(theme.palette.error.main).alpha(0.1).toString(),
    border: `1px solid ${theme.palette.error.main}`,
  },
  routePlanManagementButtons: {
    ...common.flexColumn,
    rowGap: '1rem',
  },
  warehouseList: {
    ...common.flexColumn,
    rowGap: '0.5rem',
  },
  scheduleLabel: {
    minWidth: 120,
  },
}));

const RoutePlanInfo: React.FC<{ item: RoutePlan }> = ({ item }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={classes.routePlanInfoContainer}>
      <div role="head">{routePlanStrings.serviceName}</div>
      <div role="data">
        {item.service_name}
        <div role="divider" />
      </div>

      <div role="head">{routePlanStrings.route2}</div>
      <div role="data">
        {item.name}
        <div role="divider" />
      </div>

      <div role="head">{agencyStrings.agency}</div>
      <div role="data">
        <Link
          to={`${url('/admin/widgets/AgencyDetails')}?id=${item.agency.id}`}
          className={classes.link}
        >
          {item.agency.name_en_short}
        </Link>

        <div role="divider" />
      </div>

      <div role="head">{routePlanStrings.receivingWarehouse}</div>

      <div role="data" className={classes.warehouseList}>
        {item.receiving_warehouses.length
          ? item.receiving_warehouses.map((r) => <WarehouseDisplay item={r} key={r.id} />)
          : '-'}
        <div role="divider" />
      </div>

      <div role="head">{routePlanStrings.exportWarehouse}</div>

      <div role="data">
        {item.export_warehouse ? <WarehouseDisplay item={item.export_warehouse} /> : '-'}{' '}
        <div role="divider" />
      </div>

      <div role="head">{vesselScheduleStrings.pol}</div>

      <div role="data">
        {getCityLabelByName(item.pol)}
        <div role="divider" />
      </div>

      <div role="head">{vesselScheduleStrings.pod}</div>

      <div role="data">{getCityLabelByName(item.pod)}</div>
    </div>
  );
};

interface ScheduleDisplayEditProps {
  name: string;
  index: number;
  onDelete: () => void;
  isEditing: boolean;
  schedule?: RoutePlanSchedule;
}

const ScheduleDisplayEdit: React.FC<ScheduleDisplayEditProps> = ({
  name: parentName,
  index,
  onDelete,
  isEditing,
  schedule,
}) => {
  const classes = { ...useCommonStyles(), ...useStyles() };
  const [scheduleField, ,] = useField<RoutePlanSchedule[]>('schedules');

  return (
    <div>
      <div
        className={classes.routePlanManagementInfoTitle}
        style={{ paddingBottom: '0.5rem', paddingTop: '0.5rem' }}
      >
        {`${routePlanStrings.schedule} ${index + 1}`}
        {isEditing && scheduleField.value?.length > 1 && (
          <Button
            color="grey"
            shape="round"
            size="small"
            text={formStrings.delete}
            onClick={onDelete}
            type="button"
          />
        )}
      </div>

      <div style={{ height: 15 }} />

      {routePlanScheduleFields.map(({ name, label }) => (
        <>
          <div className={clsx(classes.flexInline, classes.itemsCenter, classes.justifyBetween)}>
            <div className={classes.scheduleLabel}>{label}</div>
            {isEditing ? (
              <SimpleTextInput
                textInputWidth={40}
                name={joinKeyPath(parentName, index, name)}
                choose
                options={iota(7).map((i) => ({ label: weekdays[i], value: i }))}
              />
            ) : (
              <div>{weekdays[schedule?.[name] ?? -1] || '-'}</div>
            )}
          </div>
          <div style={{ height: 10 }} />
        </>
      ))}
    </div>
  );
};

const ScheduleArrayEditDisplay: React.FC<{
  isEditing: boolean;
  schedules: RoutePlanSchedule[];
}> = ({ isEditing, schedules }) => {
  const [scheduleField, ,] = useField<RoutePlanSchedule[]>('schedules');

  return (
    <FieldArray
      name="schedules"
      render={({
        remove,
        insert,
      }: {
        remove: (index: number) => void;
        insert: (index: number, value: RoutePlanScheduleForm) => void;
      }) => {
        return (
          <>
            {scheduleField.value?.map((value, index) => (
              <ScheduleDisplayEdit
                key={index}
                name="schedules"
                index={index}
                isEditing={isEditing}
                schedule={schedules[index]}
                onDelete={() => remove(index)}
              />
            ))}
            {isEditing && (
              <div>
                <Button
                  color="primary"
                  size="small"
                  text={formStrings.add}
                  shape="round"
                  type="button"
                  onClick={() =>
                    insert(scheduleField.value?.length ?? 0, routePlanScheduleFormInitialValues)
                  }
                />
              </div>
            )}
          </>
        );
      }}
    />
  );
};

const RoutePlanManagementInfo: React.FC<{ item: RoutePlan; canEdit: boolean }> = ({
  item,
  canEdit,
}) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const deleteDialog = useConfirmDialog();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const user = useUser();
  const isAdmin = user.data?.role === 'admin' || user.data?.is_superuser;
  const history = useHistory();

  const renderDisableDialog = () => (
    <ActionableDialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
      <ActionableDialogTitle>{formStrings.unlock}</ActionableDialogTitle>
      <ActionableDialogContent style={{ padding: 55 }}>
        {strings.diablePrompt()}
      </ActionableDialogContent>
      <ActionableDialogActions justifyContent="center">
        <Button
          color="grey"
          size="small"
          text={formStrings.no}
          shape="round"
          type="button"
          onClick={() => setIsDialogOpen(false)}
        />
        <div style={{ width: 20 }} />
        <Button
          color="primary"
          size="small"
          text={formStrings.yes}
          shape="round"
          type="button"
          onClick={async () => {
            await patchRoutePlanForDisable.mutateAsync({ disabled: true });
            setIsDialogOpen(false);
          }}
        />
      </ActionableDialogActions>
    </ActionableDialog>
  );

  const initialValues = useMemo(
    () =>
      item.schedules?.length
        ? pick(item, ['schedules'])
        : { schedules: [routePlanScheduleFormInitialValues] },
    [item],
  );

  const validate = useMemo(() => createValidate(routePlanSchedulesForm), [routePlanSchedulesForm]);

  const patchRoutePlan = usePatchRoutePlan(item.id);
  const patchRoutePlanForDisable = usePatchRoutePlan(item.id);
  const deleteRoutePlan = useDeleteRoutePlan(item.id);

  return (
    <>
      {renderDisableDialog()}
      {deleteDialog.renderDialog()}
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validate={validate}
        onSubmit={async (_values) => {
          const values = unwrap(routePlanSchedulesForm, _values);
          await patchRoutePlan.mutateAsync(values).then(() => setIsEditing(false));
        }}
      >
        <Form className={classes.routePlanManagememntInfoContainer}>
          <div className={classes.routePlanManagementInfoSection}>
            <FormAlert
              mutation={patchRoutePlan}
              showSuccess
              successMessage={strings.patchRoutePlanSuccess}
            />
            <SubmittingOverlay />

            <ScheduleArrayEditDisplay isEditing={isEditing} schedules={item.schedules ?? []} />

            {isEditing && (
              <Button
                color="primary"
                size="small"
                text={formStrings.update}
                shape="round"
                type="submit"
              />
            )}
            {!isEditing && (
              <Button
                color="primary"
                size="small"
                text={strings.editSchedule}
                shape="round"
                type="button"
                disabled={!isAdmin}
                onClick={() => setIsEditing(true)}
              />
            )}
          </div>
          {isAdmin && (
            <div className={classes.routePlanManagementInfoSection}>
              <FormAlert
                mutation={patchRoutePlanForDisable}
                showSuccess
                successMessage={
                  item.disabled ? strings.disableRoutePlanSuccess : strings.enableRoutePlanSuccess
                }
              />

              <FormAlert mutation={deleteRoutePlan} />

              <div className={classes.routePlanManagementButtons}>
                {item.disabled ? (
                  <>
                    <Button
                      // unmount the button to avoid color transition
                      key={String(item.disabled)}
                      isLoading={patchRoutePlanForDisable.isLoading}
                      color="accent"
                      shape="round"
                      size="small"
                      text={strings.enableTheRoutePlan}
                      type="button"
                      onClick={async () =>
                        await patchRoutePlanForDisable.mutateAsync({ disabled: false })
                      }
                    />

                    <Button
                      isLoading={deleteRoutePlan.isLoading}
                      color="error"
                      shape="round"
                      size="small"
                      text={strings.deleteTheRoutePlan}
                      type="button"
                      disabled={!canEdit}
                      title={!canEdit ? strings.deleteDisabledReasonUsing : undefined}
                      onClick={async () => {
                        if (
                          (await deleteDialog.confirm({
                            message: '本当に削除しますか？',
                          })) === 'confirmed'
                        ) {
                          await deleteRoutePlan.mutateAsync();
                          history.replace(url('/admin/widgets/RoutePlanManagement'));
                        }
                      }}
                    />
                  </>
                ) : (
                  <Button
                    key={String(item.disabled)}
                    isLoading={patchRoutePlanForDisable.isLoading}
                    color="custom"
                    shape="round"
                    size="small"
                    text={strings.disableTheRoutePlan}
                    type="button"
                    className={classes.disableButton}
                    onClick={() => setIsDialogOpen(true)}
                  />
                )}
              </div>
            </div>
          )}
        </Form>
      </Formik>
    </>
  );
};

const RouteMembers: React.FC<{ members: Member[] }> = ({ members }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={classes.routeMembersContainer}>
      <div role="head">{strings.fcsNo}</div>
      <div role="head">{companyStrings.company_name}</div>

      {members.map((m) => (
        <Fragment key={m.id}>
          <div role="data">{m.member_no || '-'}</div>
          <div role="data">
            {m.company_name || '-'}

            <a
              className={clsx(classes.link, classes.floatRight)}
              href={`${url('/admin/members/MemberDetails')}?id=${m.id}`}
              target="_blank"
            >
              {strings.details}
            </a>
          </div>
        </Fragment>
      ))}

      {members.length === 0 && (
        <div className={classes.noRouteMembers} style={{ gridColumn: 'span 2' }}>
          {strings.noRouteMembers()}
        </div>
      )}
    </div>
  );
};

const DisabledWarning: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return <div className={classes.disabledWarning}>{strings.disabledWarning}</div>;
};

const searchType = t.type({
  id: h.number().cast(),
  page: h.omittableNumber().cast(),
});

export const RoutePlanDetail: React.FC = () => {
  const { search } = useSearch(searchType);

  const [editOpen, setEditOpen] = useState(false);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const user = useUser();
  const routePlan = useRoutePlan(search.id);
  const members = useMemberList({
    route_plan_id: search.id,
    page_size: 10,
    page: search.page ?? 1,
  });

  const isDisabledToMembers = (members.data?.count ?? 0) > 0 && routePlan.data?.disabled === true;

  const isAdmin = user.data?.role === 'admin' || user.data?.is_superuser;
  const canEdit =
    members.data?.count === 0 &&
    routePlan.data?.disabled === true &&
    (user.data?.role === 'admin' || user.data?.is_superuser);

  const agencyList = useAgencyList(undefined, { enabled: canEdit });

  const breadcrumb = useMemo(
    () => [
      ...getBreadcrumb(url('/admin/widgets/RoutePlanManagement')),
      routePlan.data ? `${routePlan.data?.name} - ${routePlan.data?.agency.name_en_short}` : '-',
    ],
    [routePlan.data?.name, routePlan.data?.agency.name_en_short],
  );

  return (
    <Layout
      title={routePlan.data?.name ?? ''}
      loading={routePlan.isLoading || user.isLoading}
      errors={[routePlan.error, user.error]}
      breadcrumb={breadcrumb}
    >
      {() => (
        <div className={classes.container}>
          {agencyList.data && (
            <RoutePlanAddItemDialog
              agencyList={agencyList.data}
              open={editOpen}
              routePlan={routePlan.data}
              onClose={() => setEditOpen(false)}
            />
          )}

          <div className={classes.leftColumn}>
            <div style={{ maxWidth: 600 }}>
              <FormTitle
                title={strings.routeInfo}
                editDisabled={!canEdit}
                editDisabledReason={
                  canEdit
                    ? undefined
                    : isAdmin
                    ? strings.editDisabledReasonUsing
                    : strings.editDisabledAdminOnly
                }
                editting={editOpen}
                onChangeEditting={setEditOpen}
              />
              <RoutePlanInfo item={routePlan.data!} />

              <div style={{ height: '2rem' }} />

              <FormTitle title={strings.routeMembers} noEdit />

              {isDisabledToMembers && <DisabledWarning />}

              <QueryContainer queries={[members]} style={{ height: 600 }}>
                <RouteMembers members={members.data?.results ?? []} />
              </QueryContainer>

              {members.data && members.data.count > 10 && (
                <PaginationWithSearchQuery query={members} pageSize={10} />
              )}
            </div>
          </div>

          <div className={classes.rightColumn}>
            <FormTitle title={strings.managementInfo} noEdit />
            <RoutePlanManagementInfo item={routePlan.data!} canEdit={!!canEdit} />
          </div>
        </div>
      )}
    </Layout>
  );
};
